import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {useClipboard} from '../../../../helpers/hooks/clipboardHookHelpers';
import {ChannelCustomModel} from '../../../../models/api/channelModel';
import {SettingsAccordionFieldLine} from '../../../common/settingsAccordionFieldLine';
import {StyledSettingsFormTextBox} from '../../../common/settingsWrapper';
import {SettingsCommonChannelFieldProps} from '../settingsCommonChannelFields';

/*
 * Props.
 */

interface ChannelApiEndpointsFieldProps extends SettingsCommonChannelFieldProps<ChannelCustomModel> {}

/*
 * Style.
 */

const StyledAnchor = styled.a`
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.palette[PaletteColorsEnum.BLUE].shade40};
  cursor: pointer;

  &:hover {
    color: ${(p) => p.theme.palette[PaletteColorsEnum.BLUE].shade50};
  }
`;

/*
 * Component.
 */

export const ChannelApiEndpointsField: FC<ChannelApiEndpointsFieldProps> = (props) => {
  const {copy} = useClipboard();

  return (
    <SettingsAccordionFieldLine
      title={<Trans id="settingschannel-apiendpoints-title">API Endpoints</Trans>}
      subtitle={
        <Trans id="settingschannel-apiendpoints-subtitle">
          Use these URLs to send / receive messages.{' '}
          <StyledAnchor
            href="https://dev.frontapp.com/docs/creating-a-custom-channel#sending-and-receiving-messages"
            rel="noopener noreferrer"
            target="_blank"
          >
            Read documentation
          </StyledAnchor>
          .
        </Trans>
      }
    >
      <StyledSettingsFormTextBox
        $width={450}
        label={<Trans id="settingschannel-incoming">Incoming</Trans>}
        size={VisualSizesEnum.MEDIUM}
        value={props.edited.apiUrl}
        message={
          <Trans id="settingschannel-incoming-copy">Call this URL to receive a message in Front.</Trans>
        }
        onCopy={() => {
          copy(props.edited.apiUrl);
        }}
      />
      <StyledSettingsFormTextBox
        $width={450}
        label={<Trans id="settingschannel-outcoming">Outcoming</Trans>}
        size={VisualSizesEnum.MEDIUM}
        value={props.edited.preferences.webhookUrlOut}
        onChange={(newValue) => {
          props.onPreferencesChange({
            webhookUrlOut: newValue,
          });
        }}
        message={
          <Trans id="settingschannel-outcoming-copy">
            Front will POST to this URL whenever you send a message from this channel.
          </Trans>
        }
      />
    </SettingsAccordionFieldLine>
  );
};
